import AdminTable from '../../../components/admin/AdminTable';
import React, { useEffect, useState, useCallback } from 'react';
import {
    Plus,
    History,
    Edit,
    Trash2,
    Users,
    Calendar,
    Search,
    X,
    CheckCircle,
    Clock,
    AlertCircle,
    Ticket,
    Sparkles,
    Upload,
    Image as ImageIcon,
    Check,
} from 'lucide-react';
import {
    fetchEventosAdmin,
    createEvento,
    updateEvento,
    deleteEvento,
    fetchInscripcionesEvento,
    Evento,
    InscripcionEvento,
} from '../../../services/eventoService';
import Pagination from '../../../components/Pagination/Pagination';

export const EventosPage: React.FC = () => {
    const [eventos, setEventos] = useState<Evento[]>([]);
    const [loading, setLoading] = useState(false);
    const [filterTipo, setFilterTipo] = useState<'todos' | 'general' | 'networking'>('todos');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    // Modales
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [editingEvento, setEditingEvento] = useState<Evento | null>(null);
    const [deletingEvento, setDeletingEvento] = useState<Evento | null>(null);
    const [viewingInscripcionesEvento, setViewingInscripcionesEvento] = useState<Evento | null>(null);
    const [inscripciones, setInscripciones] = useState<InscripcionEvento[]>([]);
    const [loadingInscripciones, setLoadingInscripciones] = useState(false);
    const [searchInscrito, setSearchInscrito] = useState('');

    const loadEventos = useCallback(async () => {
        try {
            setLoading(true);
            const data = await fetchEventosAdmin({
                tipo: filterTipo === 'todos' ? undefined : filterTipo,
            });
            setEventos(data);
        } catch (error) {
            console.error('Error cargando eventos:', error);
        } finally {
            setLoading(false);
        }
    }, [filterTipo]);

    useEffect(() => {
        loadEventos();
    }, [loadEventos]);

    const handleOpenInscripciones = async (evento: Evento) => {
        setViewingInscripcionesEvento(evento);
        setLoadingInscripciones(true);
        try {
            const data = await fetchInscripcionesEvento(evento.id);
            setInscripciones(data);
        } catch (error) {
            console.error('Error cargando inscripciones:', error);
            setInscripciones([]);
        } finally {
            setLoadingInscripciones(false);
        }
    };

    const handleDelete = async () => {
        if (!deletingEvento) return;
        try {
            await deleteEvento(deletingEvento.id);
            setEventos(prev => prev.filter(e => e.id !== deletingEvento.id));
            setDeletingEvento(null);
        } catch (error) {
            console.error('Error eliminando evento:', error);
            alert('No se pudo eliminar el evento.');
        }
    };

    // Filtro local
    const filteredEventos = eventos.filter(e => {
        const q = searchQuery.toLowerCase().trim();
        if (!q) return true;
        return (
            e.titulo.toLowerCase().includes(q) ||
            e.categoria.toLowerCase().includes(q) ||
            e.ubicacion.toLowerCase().includes(q) ||
            e.formato.toLowerCase().includes(q)
        );
    });

    const totalPages = Math.ceil(filteredEventos.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedEventos = filteredEventos.slice(startIndex, startIndex + itemsPerPage);

    // Métricas
    const totalInscritosGlobal = eventos.reduce((sum, e) => sum + (e.total_inscritos || 0), 0);
    const totalCapacidadGlobal = eventos.reduce((sum, e) => sum + (e.capacidad || 0), 0);
    const promOcupacion = totalCapacidadGlobal > 0 ? Math.round((totalInscritosGlobal / totalCapacidadGlobal) * 100) : 0;
    const totalNetworking = eventos.filter(e => e.tipo === 'networking').length;

    return (
        <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl pb-16 font-sans">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <span>Eventos & Networking</span>
                        <span className="text-xs px-2.5 py-1 bg-purple-100 text-purple-700 font-bold rounded-full">
                            CMS & Control
                        </span>
                    </h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <span className="text-purple-600 font-medium">Eventos & Networking</span>
                    </div>
                </div>

                <div className="flex gap-2 items-center w-full sm:w-auto">
                    <button
                        onClick={loadEventos}
                        className="bg-[#61257b] text-white px-3.5 py-2 rounded-lg hover:bg-[#4a1c5e] flex items-center h-[39px] text-sm font-medium transition-colors shadow-sm"
                        title="Recargar eventos"
                    >
                        <History className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                        onClick={() => setIsAddOpen(true)}
                        className="bg-[#C1348B] text-white px-4 py-2 rounded-lg hover:bg-[#8f1f64] flex items-center text-sm font-bold transition-all shadow-md hover:shadow-lg"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nuevo Evento
                    </button>
                </div>
            </div>

            {/* Metrics cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Total Eventos</p>
                        <p className="text-2xl font-extrabold text-gray-900 mt-1">{eventos.length}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center text-xl">
                        📅
                    </div>
                </div>

                <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Networking</p>
                        <p className="text-2xl font-extrabold text-purple-700 mt-1">{totalNetworking}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center text-xl">
                        🤝
                    </div>
                </div>

                <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Asistentes Inscritos</p>
                        <p className="text-2xl font-extrabold text-pink-600 mt-1">{totalInscritosGlobal}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-pink-50 text-pink-600 flex items-center justify-center text-xl">
                        🎟️
                    </div>
                </div>

                <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Ocupación Promedio</p>
                        <p className="text-2xl font-extrabold text-orange-600 mt-1">{promOcupacion}%</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center text-xl">
                        📊
                    </div>
                </div>
            </div>

            {/* Filter and search bar */}
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex gap-2 w-full md:w-auto">
                    {(['todos', 'general', 'networking'] as const).map(tipo => (
                        <button
                            key={tipo}
                            onClick={() => { setFilterTipo(tipo); setCurrentPage(1); }}
                            className={`px-4 py-2 text-xs font-bold rounded-lg uppercase tracking-wider transition-all ${
                                filterTipo === tipo
                                    ? 'bg-[#61257B] text-white shadow'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {tipo === 'todos' ? 'Todos' : tipo === 'general' ? 'Eventos' : 'Networking'}
                        </button>
                    ))}
                </div>

                <div className="relative w-full md:w-80">
                    <input
                        type="text"
                        placeholder="Buscar por título, categoría, lugar..."
                        value={searchQuery}
                        onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                        className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all"
                    />
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                </div>
            </div>

            {/* Event Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <AdminTable className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 text-gray-500 text-xs font-bold uppercase tracking-wider">
                            <tr>
                                <th className="px-6 py-3.5 text-left">Evento</th>
                                <th className="px-6 py-3.5 text-left">Tipo</th>
                                <th className="px-6 py-3.5 text-left">Estado</th>
                                <th className="px-6 py-3.5 text-left">Capacidad & Aforo</th>
                                <th className="px-6 py-3.5 text-center">Inscritos</th>
                                <th className="px-6 py-3.5 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-sm">
                            {displayedEventos.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-gray-400">
                                        No se encontraron eventos registrados con los criterios seleccionados.
                                    </td>
                                </tr>
                            ) : (
                                displayedEventos.map(evento => {
                                    const pct = evento.porcentaje_ocupacion || 0;
                                    return (
                                        <tr key={evento.id} className="hover:bg-purple-50/40 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    {evento.imagen ? (
                                                        <img
                                                            src={evento.imagen}
                                                            alt={evento.titulo}
                                                            className="w-11 h-11 rounded-xl object-cover border border-purple-100 flex-shrink-0 shadow-sm"
                                                        />
                                                    ) : (
                                                        <div
                                                            className="w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                                                            style={{ background: `${evento.color || '#61257B'}20` }}
                                                        >
                                                            {evento.icono || (evento.tipo === 'networking' ? '🤝' : '🎯')}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div className="font-bold text-gray-900 leading-tight">
                                                            {evento.titulo}
                                                        </div>
                                                        <div className="text-xs text-gray-500 mt-0.5">
                                                            📅 {evento.fecha} · 📍 {evento.ubicacion}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="px-6 py-4">
                                                <span
                                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold capitalize ${
                                                        evento.tipo === 'networking'
                                                            ? 'bg-purple-100 text-purple-700'
                                                            : 'bg-blue-100 text-blue-700'
                                                    }`}
                                                >
                                                    {evento.tipo}
                                                </span>
                                            </td>

                                            <td className="px-6 py-4">
                                                <span
                                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold capitalize ${
                                                        evento.estado === 'en curso'
                                                            ? 'bg-amber-100 text-amber-700'
                                                            : evento.estado === 'proximo'
                                                            ? 'bg-emerald-100 text-emerald-700'
                                                            : 'bg-gray-100 text-gray-600'
                                                    }`}
                                                >
                                                    {evento.estado}
                                                </span>
                                            </td>

                                            <td className="px-6 py-4">
                                                <div className="w-44">
                                                    <div className="flex justify-between text-xs font-medium text-gray-600 mb-1">
                                                        <span>{evento.total_inscritos} / {evento.capacidad}</span>
                                                        <span className="font-bold">{pct}%</span>
                                                    </div>
                                                    <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full rounded-full transition-all duration-500"
                                                            style={{
                                                                width: `${Math.min(100, pct)}%`,
                                                                background: pct >= 80 ? '#C1348B' : (evento.color || '#61257B'),
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="px-6 py-4 text-center">
                                                <button
                                                    onClick={() => handleOpenInscripciones(evento)}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 hover:bg-purple-100 rounded-lg text-xs font-bold transition-colors"
                                                >
                                                    <Users className="w-3.5 h-3.5" />
                                                    <span>{evento.total_inscritos} personas</span>
                                                </button>
                                            </td>

                                            <td className="px-6 py-4 text-right whitespace-nowrap">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => setEditingEvento(evento)}
                                                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                                        title="Editar evento"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => setDeletingEvento(evento)}
                                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                                        title="Eliminar evento"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </AdminTable>
                </div>

                {totalPages > 1 && (
                    <div className="p-4 border-t border-gray-100">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            itemsPerPage={itemsPerPage}
                            totalItems={filteredEventos.length}
                        />
                    </div>
                )}
            </div>

            {/* Modal: Ver Inscritos */}
            {viewingInscripcionesEvento && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in duration-200">
                        <div className="bg-gradient-to-r from-[#451759] to-[#C1348B] p-6 text-white flex justify-between items-start">
                            <div>
                                <span className="text-xs uppercase font-bold tracking-wider px-2.5 py-1 bg-white/20 rounded-full">
                                    Lista Oficial de Asistentes
                                </span>
                                <h3 className="text-xl font-bold mt-2">{viewingInscripcionesEvento.titulo}</h3>
                                <p className="text-xs text-white/80 mt-1">
                                    Total confirmados: {inscripciones.length} personas · Aforo total: {viewingInscripcionesEvento.capacidad}
                                </p>
                            </div>
                            <button
                                onClick={() => setViewingInscripcionesEvento(null)}
                                className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-1.5 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6">
                            <div className="mb-4">
                                <input
                                    type="text"
                                    placeholder="Buscar asistente por nombre, email o código de pase..."
                                    value={searchInscrito}
                                    onChange={e => setSearchInscrito(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-purple-500"
                                />
                            </div>

                            {loadingInscripciones ? (
                                <div className="text-center py-12 text-gray-400">Cargando lista de asistentes...</div>
                            ) : inscripciones.length === 0 ? (
                                <div className="text-center py-12 text-gray-400">
                                    Aún no hay inscripciones registradas para este evento.
                                </div>
                            ) : (
                                <div className="max-h-96 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-xl">
                                    {inscripciones
                                        .filter(i => {
                                            const q = searchInscrito.toLowerCase().trim();
                                            if (!q) return true;
                                            return (
                                                i.nombre_completo.toLowerCase().includes(q) ||
                                                i.email.toLowerCase().includes(q) ||
                                                i.codigo_pase.toLowerCase().includes(q) ||
                                                (i.empresa_o_cargo && i.empresa_o_cargo.toLowerCase().includes(q))
                                            );
                                        })
                                        .map(insc => (
                                            <div key={insc.id} className="p-3.5 flex justify-between items-center hover:bg-gray-50">
                                                <div>
                                                    <div className="font-bold text-gray-900 text-sm flex items-center gap-2">
                                                        <span>{insc.nombre_completo}</span>
                                                        <span className="text-xs text-purple-700 bg-purple-50 px-2 py-0.5 rounded font-mono font-bold">
                                                            {insc.codigo_pase}
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-0.5">
                                                        {insc.email} · {insc.telefono} {insc.empresa_o_cargo && `· ${insc.empresa_o_cargo}`}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <span className="text-xs px-2 py-1 rounded-full font-bold bg-green-100 text-green-700 uppercase">
                                                        {insc.estado}
                                                    </span>
                                                    <div className="text-[11px] text-gray-400 mt-1">
                                                        {new Date(insc.fecha_registro).toLocaleDateString()}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            )}

                            <div className="mt-6 flex justify-end">
                                <button
                                    onClick={() => setViewingInscripcionesEvento(null)}
                                    className="px-5 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Crear / Editar Evento */}
            {(isAddOpen || editingEvento) && (
                <EventoFormModal
                    initialData={editingEvento || undefined}
                    onClose={() => { setIsAddOpen(false); setEditingEvento(null); }}
                    onSuccess={() => { setIsAddOpen(false); setEditingEvento(null); loadEventos(); }}
                />
            )}

            {/* Modal: Confirmar Eliminar */}
            {deletingEvento && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-gray-100 text-center">
                        <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4 text-xl">
                            ⚠️
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">¿Eliminar este evento?</h3>
                        <p className="text-sm text-gray-500 mb-6">
                            Estás a punto de eliminar <strong>"{deletingEvento.titulo}"</strong>. Esta acción cancelará también los registros de asistentes vinculados.
                        </p>
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => setDeletingEvento(null)}
                                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-bold"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold shadow-md"
                            >
                                Sí, eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Modal Formulario Evento (Crear y Editar) ──
function EventoFormModal({
    initialData,
    onClose,
    onSuccess,
}: {
    initialData?: Evento;
    onClose: () => void;
    onSuccess: () => void;
}) {
    const isEdit = Boolean(initialData);
    const [titulo, setTitulo] = useState(initialData?.titulo || '');
    const [categoria, setCategoria] = useState(initialData?.categoria || 'Networking');
    const [tipo, setTipo] = useState<'general' | 'networking'>(initialData?.tipo || 'networking');
    const [fecha_inicio, setFechaInicio] = useState(initialData?.fecha_inicio || '');
    const [hora, setHora] = useState(initialData?.hora || '7:00 PM');
    const [formato, setFormato] = useState(initialData?.formato || 'Presencial');
    const [capacidad, setCapacidad] = useState(String(initialData?.capacidad || 50));
    const [ubicacion, setUbicacion] = useState(initialData?.ubicacion || 'Co Marca — Calacoto');
    const [descripcion, setDescripcion] = useState(initialData?.descripcion || '');
    const [estado, setEstado] = useState(initialData?.estado || 'proximo');
    const [tagsText, setTagsText] = useState((initialData?.tags || []).join(', '));
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    // Estado para la imagen
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(initialData?.imagen || null);
    const [removeExistingImage, setRemoveExistingImage] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);

    const handleTipoChange = (newTipo: 'general' | 'networking') => {
        setTipo(newTipo);
        if (newTipo === 'networking') {
            if (categoria === 'Conferencia' || !categoria) setCategoria('Networking');
        } else {
            if (categoria === 'Networking' || !categoria) setCategoria('Conferencia');
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
            setRemoveExistingImage(false);
        }
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreview(null);
        setRemoveExistingImage(true);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError(null);

        const cleanTitulo = titulo.trim();
        if (!cleanTitulo) {
            setFormError('Por favor ingresa un título para el evento.');
            return;
        }

        const cleanFechaInicio = fecha_inicio.trim();
        if (!cleanFechaInicio) {
            setFormError('Por favor selecciona la fecha del evento.');
            return;
        }

        setSubmitting(true);
        try {
            const tags = tagsText.split(',').map(t => t.trim()).filter(Boolean);
            
            // Generar fecha en texto amigable a partir de fecha_inicio
            const dateObj = new Date(cleanFechaInicio + 'T00:00:00'); // Evitar desfase de zona horaria
            const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
            const friendlyFecha = `${dateObj.getDate()} ${meses[dateObj.getMonth()]} ${dateObj.getFullYear()}`;

            const formData = new FormData();
            formData.append('titulo', cleanTitulo);
            formData.append('categoria', categoria.trim() || (tipo === 'networking' ? 'Networking' : 'General'));
            formData.append('tipo', tipo);
            formData.append('fecha_inicio', cleanFechaInicio);
            formData.append('fecha', friendlyFecha);
            formData.append('hora', hora.trim() || '7:00 PM');
            formData.append('formato', formato.trim() || 'Presencial');
            formData.append('capacidad', String(parseInt(capacidad, 10) || 50));
            formData.append('ubicacion', ubicacion.trim() || 'Co Marca — Calacoto');
            formData.append('descripcion', descripcion.trim());
            formData.append('estado', estado);
            formData.append('tags', JSON.stringify(tags));

            if (imageFile) {
                formData.append('imagen', imageFile);
            } else if (removeExistingImage) {
                formData.append('imagen', '');
            }

            if (isEdit && initialData) {
                await updateEvento(initialData.id, formData);
            } else {
                await createEvento(formData);
            }
            onSuccess();
        } catch (error: any) {
            console.error('Error guardando evento:', error);
            let msg = 'Ocurrió un error al guardar el evento.';
            if (error?.response?.data) {
                const data = error.response.data;
                if (typeof data === 'string') {
                    msg = data;
                } else if (typeof data === 'object') {
                    const details = Object.entries(data)
                        .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`)
                        .join(' | ');
                    msg = details || msg;
                }
            }
            setFormError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden border border-gray-100 max-h-[92vh] flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#451759] to-[#61257B] p-5 text-white flex justify-between items-center flex-shrink-0">
                    <div>
                        <h3 className="text-lg font-bold">
                            {isEdit ? 'Editar Evento o Sesión' : 'Nuevo Evento o Sesión de Networking'}
                        </h3>
                        <p className="text-xs text-purple-200 mt-0.5">
                            Configura los detalles, imagen de portada y fecha para la landing y el portal.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-5 flex-1">
                    {/* Error Banner */}
                    {formError && (
                        <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-start gap-2.5 animate-fadeIn">
                            <AlertCircle className="w-5 h-5 flex-shrink-0 text-red-500 mt-0.5" />
                            <div className="flex-1 font-medium">{formError}</div>
                        </div>
                    )}

                    {/* SECCIÓN 1: Información Básica */}
                    <div className="space-y-3">
                        <div className="border-b border-gray-100 pb-1">
                            <span className="text-xs font-bold text-purple-700 uppercase tracking-wider">
                                1. Información Principal
                            </span>
                        </div>

                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                                Título del Evento *
                            </label>
                            <input
                                type="text"
                                required
                                value={titulo}
                                onChange={e => setTitulo(e.target.value)}
                                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 font-medium"
                                placeholder="Ej: Noche de Founders & Networking VIP"
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                                    Tipo de Evento *
                                </label>
                                <select
                                    value={tipo}
                                    onChange={e => handleTipoChange(e.target.value as any)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-purple-500 bg-white font-medium"
                                >
                                    <option value="networking">🤝 Networking</option>
                                    <option value="general">🎯 Evento General</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                                    Categoría
                                </label>
                                <input
                                    type="text"
                                    value={categoria}
                                    onChange={e => setCategoria(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-purple-500"
                                    placeholder="Ej: Networking, Conferencia..."
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                                    Estado *
                                </label>
                                <select
                                    value={estado}
                                    onChange={e => setEstado(e.target.value as any)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-purple-500 bg-white font-medium"
                                >
                                    <option value="proximo">Próximo</option>
                                    <option value="en curso">En curso</option>
                                    <option value="pasado">Pasado / Realizado</option>
                                    <option value="cancelado">Cancelado</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* SECCIÓN 2: Imagen y Visual */}
                    <div className="space-y-3 pt-2">
                        <div className="border-b border-gray-100 pb-1">
                            <span className="text-xs font-bold text-purple-700 uppercase tracking-wider">
                                2. Imagen de Portada & Identificador Visual
                            </span>
                        </div>

                        {/* Input de Imagen */}
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5 flex items-center justify-between">
                                <span>Imagen de Portada (Recomendado)</span>
                                {imagePreview && (
                                    <span className="text-emerald-600 font-semibold text-[11px] flex items-center gap-1">
                                        <Check className="w-3.5 h-3.5" /> Imagen cargada
                                    </span>
                                )}
                            </label>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/jpg"
                                onChange={handleImageChange}
                                className="hidden"
                                id="evento-image-upload"
                            />

                            {imagePreview ? (
                                <div className="relative rounded-xl overflow-hidden border border-purple-200 bg-gray-50 group">
                                    <img
                                        src={imagePreview}
                                        alt="Vista previa"
                                        className="w-full h-44 object-cover"
                                    />
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                        <label
                                            htmlFor="evento-image-upload"
                                            className="cursor-pointer px-3 py-1.5 bg-white text-gray-800 rounded-lg text-xs font-bold shadow hover:bg-gray-100 flex items-center gap-1.5 transition-colors"
                                        >
                                            <Upload className="w-3.5 h-3.5" /> Cambiar
                                        </label>
                                        <button
                                            type="button"
                                            onClick={handleRemoveImage}
                                            className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-bold shadow hover:bg-red-700 flex items-center gap-1.5 transition-colors"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" /> Quitar
                                        </button>
                                    </div>
                                    <div className="p-2 bg-white flex justify-between items-center text-xs text-gray-600">
                                        <span className="truncate">
                                            {imageFile ? imageFile.name : 'Imagen actual guardada'}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={handleRemoveImage}
                                            className="text-red-500 hover:text-red-700 font-semibold ml-2"
                                        >
                                            Eliminar
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <label
                                    htmlFor="evento-image-upload"
                                    className="cursor-pointer border-2 border-dashed border-gray-200 hover:border-purple-400 rounded-xl p-5 flex flex-col items-center justify-center gap-2 bg-purple-50/20 hover:bg-purple-50/40 transition-all text-center"
                                >
                                    <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
                                        <ImageIcon className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold text-gray-700">
                                            Haz clic para seleccionar o subir una imagen de portada
                                        </p>
                                        <p className="text-[11px] text-gray-400 mt-0.5">
                                            PNG, JPG o WebP (Recomendado 1200x630 px)
                                        </p>
                                    </div>
                                </label>
                            )}
                        </div>
                    </div>

                    {/* SECCIÓN 3: Fechas y Logística */}
                    <div className="space-y-3 pt-2">
                        <div className="border-b border-gray-100 pb-1">
                            <span className="text-xs font-bold text-purple-700 uppercase tracking-wider">
                                3. Fecha, Horario y Lugar
                            </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                                    Fecha del Evento *
                                </label>
                                <input
                                    type="date"
                                    required
                                    value={fecha_inicio}
                                    onChange={e => setFechaInicio(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-purple-500"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                                    Hora
                                </label>
                                <input
                                    type="text"
                                    value={hora}
                                    onChange={e => setHora(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-purple-500"
                                    placeholder="Ej: 7:00 PM"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                                    Capacidad (Cupos) *
                                </label>
                                <input
                                    type="number"
                                    required
                                    min="1"
                                    value={capacidad}
                                    onChange={e => setCapacidad(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-purple-500"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                                    Formato
                                </label>
                                <input
                                    type="text"
                                    value={formato}
                                    onChange={e => setFormato(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-purple-500"
                                    placeholder="Ej: Presencial, Híbrido..."
                                />
                            </div>

                            <div className="sm:col-span-2">
                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                                    Ubicación
                                </label>
                                <input
                                    type="text"
                                    value={ubicacion}
                                    onChange={e => setUbicacion(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-purple-500"
                                    placeholder="Co Marca — Calacoto"
                                />
                            </div>
                        </div>
                    </div>

                    {/* SECCIÓN 4: Descripción y Etiquetas */}
                    <div className="space-y-3 pt-2">
                        <div className="border-b border-gray-100 pb-1">
                            <span className="text-xs font-bold text-purple-700 uppercase tracking-wider">
                                4. Contenido & Etiquetas
                            </span>
                        </div>

                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                                Descripción del Evento
                            </label>
                            <textarea
                                rows={3}
                                value={descripcion}
                                onChange={e => setDescripcion(e.target.value)}
                                className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-purple-500 resize-none"
                                placeholder="Describe el objetivo del evento, temática, público objetivo o dinámica de networking..."
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                                Etiquetas (separadas por coma)
                            </label>
                            <input
                                type="text"
                                value={tagsText}
                                onChange={e => setTagsText(e.target.value)}
                                className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-purple-500"
                                placeholder="Startups, Inversión, Coctel, Tech"
                            />
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="pt-4 flex justify-end gap-3 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-bold transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-6 py-2 bg-gradient-to-r from-[#61257B] to-[#C1348B] hover:opacity-95 text-white rounded-xl text-sm font-bold shadow-md flex items-center gap-2 transition-all disabled:opacity-50"
                        >
                            {submitting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Guardando...
                                </>
                            ) : (
                                isEdit ? 'Actualizar Evento' : 'Crear Evento'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default EventosPage;

